import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Chip, Collapse, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import GroupsOutlinedIcon     from '@mui/icons-material/GroupsOutlined'
import TrendingUpIcon         from '@mui/icons-material/TrendingUp'
import TrendingDownIcon       from '@mui/icons-material/TrendingDown'
import ShowChartIcon          from '@mui/icons-material/ShowChart'
import WarningAmberIcon       from '@mui/icons-material/WarningAmber'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, LabelList, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_MID, WARNING, WARNING_LIGHT, GREEN, RED, matchRow, scopeTotals, displayName, Kpi, Pct } from './index'
import { fetchTrendDaily } from './engine'

/* ─── Formatters ─────────────────────────────────────────────────────────── */
const nf  = n  => (n == null || isNaN(n)) ? '—' : Math.round(n).toLocaleString('en-IN')

/* ─── Shared UI atoms ────────────────────────────────────────────────────── */
function SegBadge({ seg }) {
  return (
    <Box component='span' sx={{
      fontSize: '0.6rem', fontWeight: 800, px: 0.8, py: 0.25, borderRadius: 0.5, letterSpacing: 0.3,
      background: seg === 'ENT' ? PRIMARY_LIGHT : WARNING_LIGHT,
      color:      seg === 'ENT' ? PRIMARY       : WARNING,
    }}>
      {seg}
    </Box>
  )
}

/* ─── KPI card ───────────────────────────────────────────────────────────── */
/* ─── Section card ───────────────────────────────────────────────────────── */
function SCard({ title, sub, badge, action, children }) {
  return (
    <Box sx={{ background: '#fff', borderRadius: '12px', border: '1px solid rgba(145,158,171,0.24)', boxShadow: 'none', overflow: 'hidden', mb: 2 }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.24)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e' }}>{title}</Typography>
          {sub && <Typography sx={{ fontSize: '0.7rem', color: '#9a9ab0', mt: 0.2 }}>{sub}</Typography>}
        </Box>
        {badge}
        {action}
      </Box>
      {children}
    </Box>
  )
}

/* ─── Table header cell ──────────────────────────────────────────────────── */
function Th({ children, col, sortCol, sortDir, onSort, right, minW = 48, grow }) {
  const active = sortCol === col
  return (
    <TableCell
      align={right ? 'right' : 'left'}
      onClick={col ? () => onSort(col) : undefined}
      sx={{
        fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
        color: active ? PRIMARY : '#a0a0b8',
        background: '#F4F6F8',
        borderBottom: `2px solid ${PRIMARY_MID}`,
        whiteSpace: 'nowrap', minWidth: minW,
        // width:100% on one cell of an auto-layout table makes that cell soak up
        // all leftover width while the rest stay at their minWidth.
        ...(grow ? { width: '100%' } : {}),
        lineHeight: 1.3, verticalAlign: 'bottom', py: 1.25,
        cursor: col ? 'pointer' : 'default',
        position: 'sticky', top: 0, zIndex: 2,
        '&:hover': col ? { color: PRIMARY } : {},
      }}
    >
      {children}{active && <span style={{ fontSize: 8, marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </TableCell>
  )
}

/* ─── Goal status ────────────────────────────────────────────────────────── */
function GoalTag({ g }) {
  if (g == null || isNaN(g)) return null
  if (g >= 10) return <Chip label='On track' size='small' sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, background: '#dcfce7', color: '#166534', borderRadius: '6px' }} />
  if (g >= 0)  return <Chip label='At risk'  size='small' sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, background: '#fef9c3', color: '#854d0e', borderRadius: '6px' }} />
  return           <Chip label='Behind'    size='small' sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, background: '#FFE7D9', color: '#B72136', borderRadius: '6px' }} />
}

/* ─── Trend area chart ───────────────────────────────────────────────────── */
const chartLabel = v => (v == null || !v) ? '' : (v >= 1000 ? (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : Math.round(v))

function TrendChart({ series, mlab }) {
  if (!series?.length || series.every(v => !v)) {
    return <Box sx={{ p: 4, textAlign: 'center', color: '#bbb', fontSize: '0.82rem' }}>No trend data available.</Box>
  }
  const data = series.map((v, i) => ({ name: (mlab[i] || '').replace(' ', '\n'), value: v }))
  const peak = Math.max(...series)
  return (
    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
      <ResponsiveContainer width='100%' height={190}>
        <AreaChart data={data} margin={{ top: 18, right: 20, left: 10, bottom: 0 }}>
          <defs>
            <linearGradient id='pgrd' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='5%'  stopColor={PRIMARY} stopOpacity={0.3} />
              <stop offset='95%' stopColor={PRIMARY} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(145,158,171,0.2)' vertical={false} />
          <XAxis dataKey='name' tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v} width={38} />
          <RTooltip
            formatter={v => [nf(v), 'Shipments']}
            contentStyle={{ fontSize: 11, borderRadius: 10, border: `1px solid ${PRIMARY_MID}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
          />
          <Area type='monotone' dataKey='value' stroke={PRIMARY} strokeWidth={2.5} fill='url(#pgrd)' dot={{ r: 2.5, fill: PRIMARY, strokeWidth: 0 }} activeDot={{ r: 5, fill: PRIMARY }}>
            <LabelList dataKey='value' position='top' formatter={chartLabel} style={{ fontSize: 9, fontWeight: 700, fill: '#555' }} />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
      <Typography sx={{ textAlign: 'right', fontSize: '0.68rem', color: '#a0a0b8', mt: 0.5 }}>
        Peak: <strong style={{ color: PRIMARY }}>{nf(peak)}</strong> / month
      </Typography>
    </Box>
  )
}

/* ─── WoW / DoD trend charts (fed by /trend/daily, 92 days) ─────────────── */
const DM = iso => {
  const d = new Date(iso + 'T00:00:00')
  return d.getDate() + ' ' + d.toLocaleString('en', { month: 'short' })
}

// Trailing 7-day windows ending today, oldest→newest — same windowing the
// summary's WoW% uses (last7 vs prev7), extended to 12 weeks.
function buildWowData(dailyTotals) {
  const totals = dailyTotals || []
  const weeks = []
  for (let end = totals.length; end - 7 >= 0 && weeks.length < 12; end -= 7) {
    const slice = totals.slice(end - 7, end)
    weeks.unshift({
      name: DM(slice[0].date),
      value: slice.reduce((s, d) => s + (d.total || 0), 0),
    })
  }
  return weeks
}

// One line per calendar month (current + 2 prior), x = day of month — each
// line shows that month's shipments on each day, all three overlaid.
function buildDodData(dates, dailyTotals) {
  const byDate = {}
  ;(dailyTotals || []).forEach(d => { byDate[d.date] = d.total })
  const monthKeys = []
  ;(dates || []).forEach(d => {
    const m = d.slice(0, 7)
    if (!monthKeys.includes(m)) monthKeys.push(m)
  })
  const use = monthKeys.slice(-3)
  const labels = use.map(m => {
    const d = new Date(m + '-01T00:00:00')
    return d.toLocaleString('en', { month: 'short' }) + ' ' + String(d.getFullYear()).slice(-2)
  })
  const rows = []
  for (let day = 1; day <= 31; day++) {
    const row = { day }
    use.forEach((m, i) => {
      const iso = `${m}-${String(day).padStart(2, '0')}`
      if (byDate[iso] !== undefined) row[labels[i]] = byDate[iso]
    })
    rows.push(row)
  }
  return { rows, labels }
}

function WowChart({ dailyTotals }) {
  const data = useMemo(() => buildWowData(dailyTotals), [dailyTotals])
  if (!data.length || data.every(w => !w.value)) {
    return <Box sx={{ p: 4, textAlign: 'center', color: '#bbb', fontSize: '0.82rem' }}>No weekly data available.</Box>
  }
  return (
    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
      <ResponsiveContainer width='100%' height={200}>
        <BarChart data={data} margin={{ top: 18, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(145,158,171,0.2)' vertical={false} />
          <XAxis dataKey='name' tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v} width={38} />
          <RTooltip
            formatter={v => [nf(v), 'Shipments']}
            labelFormatter={l => 'Week starting ' + l}
            contentStyle={{ fontSize: 11, borderRadius: 10, border: `1px solid ${PRIMARY_MID}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
          />
          <Bar dataKey='value' fill={PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={42}>
            <LabelList dataKey='value' position='top' formatter={chartLabel} style={{ fontSize: 9, fontWeight: 700, fill: '#555' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}

function DodChart({ dates, dailyTotals }) {
  const DOD_COLORS = ['#9a9ab0', WARNING, PRIMARY] // oldest → current month
  const { rows, labels } = useMemo(() => buildDodData(dates, dailyTotals), [dates, dailyTotals])
  if (!labels.length) {
    return <Box sx={{ p: 4, textAlign: 'center', color: '#bbb', fontSize: '0.82rem' }}>No daily data available.</Box>
  }
  return (
    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
      <ResponsiveContainer width='100%' height={240}>
        <LineChart data={rows} margin={{ top: 18, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(145,158,171,0.2)' vertical={false} />
          <XAxis dataKey='day' tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} interval={0} />
          <YAxis tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v} width={38} />
          <RTooltip
            formatter={(v, name) => [nf(v), name]}
            labelFormatter={l => 'Day ' + l}
            contentStyle={{ fontSize: 11, borderRadius: 10, border: `1px solid ${PRIMARY_MID}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {labels.map((label, i) => (
            <Line key={label} type='monotone' dataKey={label} stroke={DOD_COLORS[i] || PRIMARY}
              strokeWidth={i === labels.length - 1 ? 2.5 : 1.75}
              dot={{ r: 2, fill: DOD_COLORS[i] || PRIMARY, strokeWidth: 0 }} connectNulls={false}>
              <LabelList dataKey={label} position='top' formatter={chartLabel}
                style={{ fontSize: 8, fontWeight: 600, fill: DOD_COLORS[i] || PRIMARY }} />
            </Line>
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}

/* ─── Drill content (SAMs + movers) ─────────────────────────────────────── */
function DrillContent({ k, seg, L0, L1, L2, S1, S2 }) {
  const sams   = k.sams || []
  const growth = (seg ? (k.growth || []).filter(a => a.seg === seg) : k.growth) || []
  const degr   = (seg ? (k.degr   || []).filter(a => a.seg === seg) : k.degr)   || []

  const miniTbl = (title, rows, isGrowth) => {
    if (!rows?.length) return null
    const accent = isGrowth ? GREEN : RED
    return (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: accent, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isGrowth ? <TrendingUpIcon sx={{ fontSize: 13 }} /> : <TrendingDownIcon sx={{ fontSize: 13 }} />}
          {title}
        </Typography>
        {/* Account/blank/Seg reproduce the outer table's KAM+Accts+(ENT+SME)
            slots — Seg is widened to span both, since a single badge covers
            what ENT+SME show separately up there — so June/July/August MTD
            still land under their outer-table counterparts. Proj and growth%
            have no outer-table equivalent (no DRR concept here) so they
            aren't forced to align beyond that point. */}
        <Table size='small' sx={{ width: '100%', '& td, & th': { py: 0.75, px: 1.25 } }}>
          <TableHead>
            <TableRow sx={{ background: accent + '10' }}>
              {[
                { h: 'Account', w: 180 },
                { h: '', w: 68, blank: true },
                { h: 'Seg', w: 112 },
                { h: L0, w: 76 },
                { h: L1, w: 76 },
                { h: `${L2} MTD`, w: 92 },
                { h: 'Proj', w: 76 },
                { h: `${S1}→${S2}`, w: 88 },
              ].map(({ h, w, grow }, i) => (
                <TableCell key={i} align={i > 2 ? 'right' : 'left'} sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', color: accent, background: 'transparent', minWidth: w, whiteSpace: 'nowrap', ...(grow ? { width: '100%' } : {}) }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a, i) => (
              <TableRow key={i} hover sx={{ '&:hover td': { background: accent + '08' } }}>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e' }}>{a.name}</TableCell>
                <TableCell />
                <TableCell><SegBadge seg={a.seg} /></TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#9a9ab0' }}>{nf(a.apr)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(a.may)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(a.jun)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{nf(a.proj)}</TableCell>
                <TableCell align='right'><Pct v={a.gMJ} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 3, pb: 2.5, pt: 1.5, background: '#F9FAFB', borderTop: `1px solid ${PRIMARY_LIGHT}` }}>
      {!seg && sams.length > 0 && (
        <>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: PRIMARY, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GroupsOutlinedIcon sx={{ fontSize: 13 }} /> SAMs under {k.name}
          </Typography>
          {/* Column plan mirrors the outer KAM table's 13 slots exactly (KAM,
              Accts, ENT, SME, L0, L1, L2 MTD, S0 DRR, S1 DRR, S2 DRR,
              growth, Degrowth, vs Goal) so this drill-down's header sits
              flush under the sticky outer header — SAM has no ENT/SME/S0-DRR/
              vs-Goal data, so those four slots render as blank spacer cells
              rather than being omitted, which is what kept shifting every
              later column left of where the outer header expects it. */}
          <Table size='small' sx={{ width: '100%', '& td, & th': { py: 0.75, px: 1.25 } }}>
            <TableHead>
              <TableRow sx={{ background: '#F4F6F8' }}>
                {[
                  { h: 'SAM', w: 180 },
                  { h: 'Accts', w: 68 },
                  { h: '', w: 56, blank: true },
                  { h: '', w: 56, blank: true },
                  { h: L0, w: 76 },
                  { h: L1, w: 76 },
                  { h: `${L2} MTD`, w: 100 },
                  { h: '', w: 77, blank: true },
                  { h: `${S1} DRR`, w: 76 },
                  { h: `${S2} DRR`, w: 78 },
                  { h: `${S1}→${S2}`, w: 88 },
                  { h: 'Degrowth', w: 88 },
                ].map(({ h, w, grow }, i) => (
                  <TableCell key={i} align={i > 0 ? 'right' : 'left'} sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', color: PRIMARY, background: 'transparent', minWidth: w, whiteSpace: 'nowrap', ...(grow ? { width: '100%' } : {}) }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sams.map((s, i) => (
                <TableRow key={i} hover sx={{ '&:hover td': { background: PRIMARY_LIGHT } }}>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e' }}>{displayName(s.name)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(s.accts)}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#9a9ab0' }}>{nf(s.apr)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(s.may)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{nf(s.jun)}</TableCell>
                  <TableCell />
                  <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(s.drrMay)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{nf(s.drrJun)}</TableCell>
                  <TableCell align='right'><Pct v={s.gMJ} /></TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.72rem', color: s.degrowth ? RED : '#9a9ab0', fontWeight: s.degrowth ? 700 : 400 }}>{s.degrowth || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
      {miniTbl('Top growth sellers', growth, true)}
      {miniTbl('Top degrowth sellers', degr, false)}
      {!sams.length && !growth.length && !degr.length && (
        <Typography sx={{ fontSize: '0.78rem', color: '#bbb', py: 1 }}>No detail available.</Typography>
      )}
    </Box>
  )
}

/* ─── Main ───────────────────────────────────────────────────────────────── */
export default function P1KamSam({ vm, filters, L0, L1, L2, S0, S1, S2 }) {
  const [open,    setOpen]    = useState(new Set())
  const [sortCol, setSortCol] = useState('may')
  const [sortDir, setSortDir] = useState('desc')

  // Raw day totals for the WoW/DoD charts, fetched once for every account the
  // viewer can see (the server scopes non-admin viewers to their portfolio).
  // The page filters are applied client-side below — see filteredDaily.
  const [dailyTrend, setDailyTrend] = useState(null)
  useEffect(() => {
    let cancelled = false
    fetchTrendDaily().then(d => { if (!cancelled) setDailyTrend(d) }).catch(err => console.log(err))
    return () => { cancelled = true }
  }, [])

  const t = useMemo(() => scopeTotals(vm, filters), [vm, filters])
  const { mlab = [] } = vm

  // Segment/KAM/SAM must move the WoW and DoD charts the same way they move the
  // monthly trend — they used to ignore the filter bar entirely and always plot
  // every account. /trend/daily returns per-merchant rows, so the filtered set
  // is re-totalled here rather than re-fetched: the account list already carries
  // each merchant's seg/kam/sam, and the response has no such fields to filter on
  // server-side.
  const filteredDaily = useMemo(() => {
    if (!dailyTrend) return null
    const rows = dailyTrend.rows || []
    const dates = dailyTrend.dates || []
    const accounts = (vm.overview && vm.overview.all) || []
    const unfiltered = filters.seg === 'ALL' && filters.kam === 'ALL' && filters.sam === 'ALL'
    // oid is the only link between an account and its daily row. On an older API
    // that doesn't send it there is nothing to match on, so fall back to the
    // unfiltered totals rather than silently plotting an empty chart.
    const linkable = accounts.some(a => a.oid)
    if (unfiltered || !linkable || !rows.length || !dates.length) return dailyTrend
    const allow = new Set(accounts.filter(a => matchRow(a, filters)).map(a => a.oid))
    const totals = dates.map(() => 0)
    rows.forEach(r => {
      if (!allow.has(r.merchantOid)) return
      const daily = r.daily || []
      for (let i = 0; i < totals.length; i++) totals[i] += daily[i] || 0
    })
    return { ...dailyTrend, dailyTotals: dates.map((date, i) => ({ date, total: totals[i] })) }
  }, [dailyTrend, vm, filters])

  const scopeLabel = filters.sam !== 'ALL' ? `SAM · ${displayName(filters.sam)}`
    : filters.kam !== 'ALL' ? `KAM · ${filters.kam}`
    : filters.seg !== 'ALL' ? filters.seg
    : 'All accounts'

  const kams = useMemo(() => {
    let list = vm.kams.slice()
    if (filters.kam !== 'ALL') list = list.filter(k => k.name === filters.kam)
    if (filters.sam !== 'ALL') {
      const s = (vm.sams || []).find(s => s.name === filters.sam)
      if (s) list = list.filter(k => k.name === s.kam)
    }
    if (filters.chips.has('behind')) list = list.filter(k => k.gMJ != null && k.gMJ < filters.goal)
    const seg = filters.seg
    if (seg !== 'ALL') {
      list = list.map(k => {
        const base = seg === 'ENT' ? (k.entAgg || {}) : (k.smeAgg || {})
        return { ...base, name: k.name, sams: k.sams, growth: (k.growth || []).filter(a => a.seg === seg), degr: (k.degr || []).filter(a => a.seg === seg), _seg: seg }
      }).filter(k => k.accts > 0)
    }
    if (!sortCol) return list
    return [...list].sort((a, b) => {
      const va = a[sortCol] ?? -Infinity, vb = b[sortCol] ?? -Infinity
      return sortDir === 'asc' ? va - vb : vb - va
    })
  }, [vm, filters, sortCol, sortDir])

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('desc') }
  }
  function toggleOpen(name) {
    setOpen(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n })
  }

  const thP = { sortCol, sortDir, onSort: toggleSort }

  return (
    <Box>
      {/* KPI strip */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {[
          { label: `${L0} Shipments`,     value: nf(t.apr),     sub: 'full month',              color: '#9a9ab0', icon: null },
          { label: `${L1} Shipments`,     value: nf(t.may),     sub: 'full month',              color: '#555',   icon: null },
          { label: `${L2} MTD`,           value: nf(t.jun),     sub: 'month-to-date',           color: PRIMARY,   icon: ShowChartIcon },
          { label: `${S0} DRR`,           value: nf(t.drrApr),  sub: 'per day',                 color: '#9a9ab0' },
          { label: `${S1} DRR`,           value: nf(t.drrMay),  sub: 'per day',                 color: '#555' },
          { label: `${S2} DRR`,           value: nf(t.drrJun),  sub: <Pct v={t.gMJ} />,        color: GREEN,    icon: TrendingUpIcon },
          { label: `${S1}→${S2} Growth`,  value: <Pct v={t.gMJ} size='inherit' />, sub: 'daily run rate',     color: t.gMJ >= 0 ? GREEN : RED },
          { label: 'Degrowth accounts',   value: nf(t.degrowth), sub: 'trending down',          color: RED,      icon: WarningAmberIcon },
        ].map((k, i) => (
          <Grid item xs={6} sm={4} md={3} xl={1.5} key={i}>
            <Kpi label={k.label} value={k.value} sub={k.sub} color={k.color} icon={k.icon} />
          </Grid>
        ))}
      </Grid>

      {/* Trend chart */}
      <SCard
        title='Monthly Shipment Trend'
        badge={
          <Chip
            label={scopeLabel}
            size='small'
            sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '6px' }}
          />
        }
      >
        <TrendChart series={t.series} mlab={mlab} />
      </SCard>

      {/* Week-on-week trend — last 12 trailing weeks */}
      <SCard
        title='Week-on-Week Trend'
        sub='Total shipments per trailing 7-day week — last 12 weeks'
        badge={
          <Chip label={scopeLabel} size='small'
            sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '6px' }} />
        }
      >
        <WowChart dailyTotals={filteredDaily?.dailyTotals} />
      </SCard>

      {/* Day-on-day trend — one line per month, last 3 months overlaid */}
      <SCard
        title='Day-on-Day Trend'
        sub='Shipments on each day of the month — current month vs the two before it'
        badge={
          <Chip label={scopeLabel} size='small'
            sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '6px' }} />
        }
      >
        <DodChart dates={filteredDaily?.dates} dailyTotals={filteredDaily?.dailyTotals} />
      </SCard>

      {/* Section header */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 4, height: 18, borderRadius: 1, background: PRIMARY }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>KAM Leaderboard</Typography>
          <Chip label={`${kams.length} KAMs`} size='small' sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, background: PRIMARY_LIGHT, color: PRIMARY, ml: 0.5 }} />
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: '#9a9ab0', ml: 1.25 }}>
          Click any row to expand SAM breakdown + top growth &amp; degrowth sellers. Growth column compares <strong>daily run rate</strong>.
        </Typography>
      </Box>

      {/* KAM table */}
      <SCard title=''>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size='small' stickyHeader sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <Th {...thP}                minW={180}>KAM</Th>
                <Th {...thP} col='accts'  right minW={68}>Accts</Th>
                <Th {...thP} col='ent'    right minW={56}>ENT</Th>
                <Th {...thP} col='sme'    right minW={56}>SME</Th>
                <Th {...thP} col='apr'    right minW={76}>{L0}</Th>
                <Th {...thP} col='may'    right minW={76}>{L1}</Th>
                <Th {...thP} col='jun'    right minW={100}>{L2} MTD</Th>
                <Th {...thP} col='drrApr' right minW={77}>{S0} DRR</Th>
                <Th {...thP} col='drrMay' right minW={76}>{S1} DRR</Th>
                <Th {...thP} col='drrJun' right minW={78}>{S2} DRR</Th>
                <Th {...thP} col='gMJ'    right minW={88}>{S1}→{S2}</Th>
                <Th {...thP} col='degrowth' right minW={88}>Degrowth</Th>
                <Th right minW={90}>vs Goal</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {kams.map(k => {
                const isOpen = open.has(k.name)
                const rowBg  = k.gMJ < 0 ? '#fff8f8' : k.gMJ != null && k.gMJ < 10 ? '#fffdf0' : 'inherit'
                return (
                  <React.Fragment key={k.name}>
                    <TableRow
                      onClick={() => toggleOpen(k.name)}
                      sx={{
                        background: rowBg, cursor: 'pointer',
                        '&:hover td': { background: '#F4F6F8' },
                        '& td': { transition: 'background 0.15s' },
                      }}
                    >
                      <TableCell sx={{ py: 1.1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{
                            width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: isOpen ? PRIMARY : PRIMARY_LIGHT, transition: 'background 0.15s',
                          }}>
                            <KeyboardArrowRightIcon sx={{ fontSize: 15, color: isOpen ? '#fff' : PRIMARY, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1a1a2e' }}>{k.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{nf(k.accts)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#9a9ab0' }}>{nf(k.ent)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#9a9ab0' }}>{nf(k.sme)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#bbb' }}>{nf(k.apr)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.78rem' }}>{nf(k.may)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY }}>{nf(k.jun)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#bbb' }}>{nf(k.drrApr)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(k.drrMay)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{nf(k.drrJun)}</TableCell>
                      <TableCell align='right'><Pct v={k.gMJ} /></TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: k.degrowth ? 700 : 400, color: k.degrowth ? RED : '#bbb' }}>{k.degrowth || 0}</TableCell>
                      <TableCell align='right'><GoalTag g={k.gMJ} /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={13} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <DrillContent k={k} seg={k._seg} L0={L0} L1={L1} L2={L2} S1={S1} S2={S2} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
              {kams.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align='center' sx={{ py: 5, color: '#bbb', fontSize: '0.82rem' }}>No KAMs match the current filters.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SCard>
    </Box>
  )
}
